import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { IconFallback as Icon } from '../components/IconFallback';
import { format } from 'date-fns';
import { supabase } from '../lib/supabase';
import { ModernStickyHeader } from '../components/ModernStickyHeader';
import { SEOHead, generateArticleStructuredData } from '../components/SEOHead';
import { ARTICLE_CATEGORIES } from '../types';
import { getCustomImageUrl } from '../utils/imageProxy';
import {
  TwitterShareButton,
  LinkedinShareButton,
} from 'react-share';

interface Article {
  id: string;
  title: string;
  content: string | null;
  excerpt: string | null;
  tags: string[] | null;
  reading_time: string;
  banner_image: string | null;
  created_at: string | null;
  author_id: string | null;
  category: string | null;
  meta_title: string | null;
  meta_description: string | null;
  meta_keywords: string[] | null;
  author: {
    display_name: string | null;
    avatar_url: string | null;
  } | null;
}

export function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [article, setArticle] = useState<Article | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [imageError, setImageError] = useState(false);
  const [signedImageUrl, setSignedImageUrl] = useState<string | null>(null);
  const [prevArticle, setPrevArticle] = useState<{ slug: string; title: string } | null>(null);
  const [nextArticle, setNextArticle] = useState<{ slug: string; title: string } | null>(null);

  useEffect(() => {
    if (slug) {
      fetchArticle();
    }
  }, [slug]);

  const fetchArticle = async () => {

    if (!slug) {
      setError('Article not found');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const { data: article, error: articleError } = await supabase
        .from('articles')
        .select(`
          id,
          title,
          content,
          excerpt,
          tags,
          reading_time,
          banner_image,
          created_at,
          author_id,
          category,
          meta_title,
          meta_description,
          meta_keywords,
          views
        `)
        .eq('slug', slug)
        .eq('published', true)
        .single();

      if (articleError || !article) {
        throw new Error('Article not found');
      }

      // Increment view count
      try {
        await supabase
          .from('articles')
          .update({ views: (article.views || 0) + 1 })
          .eq('id', article.id);
      } catch (viewError) {
        console.warn('Failed to increment view count:', viewError);
      }

      // Get author information separately
      let authorInfo = null;
      if (article.author_id) {
        try {
          const { data: authorData } = await supabase
            .from('admin_profiles')
            .select('display_name, avatar_url')
            .eq('id', article.author_id)
            .single();
          
          // The avatar_url is already a full URL from the database, no need to process it further
          
          authorInfo = authorData;
        } catch (authorError) {
          console.warn('Failed to fetch author info:', authorError);
        }
      }

      setArticle({
        ...article,
        author: authorInfo,
        excerpt: article.excerpt || '',
        reading_time: `${article.reading_time || 5} min read`,
        tags: article.tags || []
      });

      // Get custom domain URL for banner image if it exists
      if (article.banner_image) {
        try {
          const customUrl = getCustomImageUrl(article.banner_image, { 
            width: 800, 
            quality: 85, 
            format: 'webp' 
          });
          setSignedImageUrl(customUrl);
        } catch (error) {
          console.warn('Failed to get custom URL for banner image:', error);
        }
      }

      // Fetch previous and next articles for navigation
      if (article.created_at) {
        await fetchAdjacentArticles(article.created_at);
      }
      
    } catch (error) {
      console.error('Error fetching article:', error);
      setError('Article not found');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAdjacentArticles = async (currentArticleDate: string) => {
    try {
      // Get previous article (older)
      const { data: prevData } = await supabase
        .from('articles')
        .select('slug, title')
        .eq('published', true)
        .lt('created_at', currentArticleDate)
        .order('created_at', { ascending: false })
        .limit(1);

      // Get next article (newer)
      const { data: nextData } = await supabase
        .from('articles')
        .select('slug, title')
        .eq('published', true)
        .gt('created_at', currentArticleDate)
        .order('created_at', { ascending: true })
        .limit(1);

      // Only set articles if they have valid slugs
      setPrevArticle(prevData?.[0]?.slug ? { slug: prevData[0].slug, title: prevData[0].title } : null);
      setNextArticle(nextData?.[0]?.slug ? { slug: nextData[0].slug, title: nextData[0].title } : null);
    } catch (error) {
      console.warn('Failed to fetch adjacent articles:', error);
    }
  };

  const handleBack = () => {
    navigate('/blog');
  };

  // Navigation links
  const navLinks = [
    { label: 'Home', href: '/' },
    { label: 'Projects', href: '/projects' },
    { label: 'Blog', href: '/blog' },
    { label: 'Contact', href: '/#contact' }
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-warm-white to-light-gray dark:from-gray-900 dark:to-gray-800">
        <ModernStickyHeader navLinks={navLinks} />
        <div className="flex items-center justify-center min-h-screen">
          <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-warm-white to-light-gray dark:from-gray-900 dark:to-gray-800">
        <ModernStickyHeader navLinks={navLinks} />
        
        {/* Background Elements */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-purple-400/20 to-blue-400/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
        </div>

        <div className="relative z-10 container mx-auto px-4 pt-32 pb-24 text-center">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Article Not Found</h1>
          <button
            onClick={handleBack}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <Icon icon="lucide:arrow-left" width="16" height="16" />
            Back
          </button>
        </div>
      </div>
    );
  }

  const shareUrl = window.location.href;
  const categoryEmoji = ARTICLE_CATEGORIES[article.category as keyof typeof ARTICLE_CATEGORIES] || '📝';

  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-warm-white to-light-gray dark:from-gray-900 dark:to-gray-800">
      <SEOHead
        title={article.meta_title || article.title}
        description={article.meta_description || article.excerpt || ''}
        keywords={article.meta_keywords || article.tags || []}
        image={article.banner_image || undefined}
        url={currentUrl}
        type="article"
        publishedTime={article.created_at || ''}
        author={article.author?.display_name || 'Anonymous'}
        structuredData={generateArticleStructuredData({
          title: article.title,
          description: article.excerpt || '',
          author: article.author?.display_name || 'Anonymous',
          publishedTime: article.created_at || '',
          image: article.banner_image || '',
          url: currentUrl
        })}
      />
      <ModernStickyHeader navLinks={navLinks} />
      
      {/* Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-32 h-32 bg-orange-200/20 dark:bg-orange-800/20 rounded-full blur-xl animate-pulse" />
        <div className="absolute top-40 right-20 w-24 h-24 bg-teal-200/20 dark:bg-teal-800/20 rounded-full blur-xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute bottom-40 left-1/4 w-40 h-40 bg-blue-200/20 dark:bg-blue-800/20 rounded-full blur-xl animate-pulse" style={{ animationDelay: '2s' }} />
      </div>

      <article className="relative z-10 container mx-auto px-4 pt-32 pb-24">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <motion.button
            onClick={handleBack}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="inline-flex items-center gap-3 mb-12 px-6 py-3 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-gray-200 dark:border-gray-700 rounded-2xl text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-800 hover:shadow-lg transition-all duration-300"
          >
            <span className="text-lg">←</span>
            Back to Blog
          </motion.button>

          {/* Breadcrumb Navigation */}
          <nav className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-8">
            <Link 
              to="/" 
              className="hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
            >
              Home
            </Link>
            <span>›</span>
            <Link 
              to="/blog" 
              className="hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
            >
              Blog
            </Link>
            <span>›</span>
            <span className="text-gray-700 dark:text-gray-300">
              {article.category || 'Uncategorized'}
            </span>
          </nav>

          {(signedImageUrl || article.banner_image) && (
            <div className="relative h-[400px] rounded-xl overflow-hidden mb-8">
              <img
                src={signedImageUrl || article.banner_image || ''}
                alt={article.title}
                className="w-full h-full object-cover"
                onError={() => {
                  // Fallback to original URL if signed URL fails
                  if (signedImageUrl && article.banner_image) {
                    setSignedImageUrl(null);
                  }
                }}
              />
            </div>
          )}

          <header className="mb-12">
            <div className="mb-6">
              <Link
                to={`/blog?category=${article.category}`}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              >
                <span className="text-sm">{categoryEmoji}</span>
                <span className="font-medium">{article.category}</span>
              </Link>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
              {article.title}
            </h1>
            
            <div className="flex flex-wrap items-center gap-6 text-gray-600 dark:text-gray-400">
              <div className="flex items-center gap-3 min-w-0">
                {!imageError && article.author?.avatar_url ? (
                  <img
                    src={getCustomImageUrl(article.author.avatar_url, { width: 80, quality: 80, format: 'webp' })}
                    alt={article.author.display_name || 'Anonymous'}
                    className="w-10 h-10 rounded-full object-cover bg-gray-200 dark:bg-gray-700 flex-shrink-0"
                    onError={() => setImageError(true)}
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                    <Icon icon="lucide:user" className="w-5 h-5 text-gray-400" />
                  </div>
                )}
                <span className="truncate">{article.author?.display_name || 'Anonymous'}</span>
              </div>
              <time dateTime={article.created_at || undefined}>
                {article.created_at && !isNaN(new Date(article.created_at).getTime()) 
                  ? format(new Date(article.created_at), 'MMMM d, yyyy')
                  : 'Unknown date'
                }
              </time>
              <span>{article.reading_time}</span>
            </div>
          </header>

          <div className="prose prose-lg dark:prose-invert max-w-none mb-12">
            {article.content && (
              <div dangerouslySetInnerHTML={{ __html: article.content }} />
            )}
          </div>

          <footer className="border-t border-gray-200 dark:border-gray-700 pt-8 space-y-8">
            {/* Tags and Social Share */}
            <div className="flex flex-wrap items-center gap-8">
              <div className="flex flex-wrap gap-2">
                {(article.tags || []).map(tag => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-gray-100 dark:bg-gray-800 rounded-full text-sm"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-600 dark:text-gray-400">Share:</span>
                <TwitterShareButton url={shareUrl} title={article.title}>
                  <Icon icon="simple-icons:x" width="20" height="20" className="text-gray-600 hover:text-primary-500" />
                </TwitterShareButton>
                <LinkedinShareButton url={shareUrl} title={article.title}>
                  <Icon icon="mdi:linkedin" width="20" height="20" className="text-gray-600 hover:text-primary-500" />
                </LinkedinShareButton>
              </div>
            </div>

            {/* Clean Article Navigation */}
            {(prevArticle || nextArticle) && (
              <div className="border-t border-gray-200/50 dark:border-gray-700/50 pt-12 mt-16">
                <div className="flex justify-between items-center">
                  {/* Previous Article */}
                  {prevArticle ? (
                    <Link
                      to={`/blog/${prevArticle.slug}`}
                      className="group flex items-center gap-3 text-left max-w-xs"
                    >
                      <div className="flex items-center justify-center w-10 h-10 bg-gray-100 dark:bg-gray-800 rounded-full group-hover:bg-blue-100 dark:group-hover:bg-blue-900/30 transition-colors duration-200">
                        <Icon icon="lucide:arrow-left" className="w-4 h-4 text-gray-600 dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Previous</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200 truncate">
                          {prevArticle.title}
                        </p>
                      </div>
                    </Link>
                  ) : (
                    <div></div>
                  )}

                  {/* Next Article */}
                  {nextArticle ? (
                    <Link
                      to={`/blog/${nextArticle.slug}`}
                      className="group flex items-center gap-3 text-right max-w-xs ml-auto"
                    >
                      <div className="min-w-0">
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Next</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200 truncate">
                          {nextArticle.title}
                        </p>
                      </div>
                      <div className="flex items-center justify-center w-10 h-10 bg-gray-100 dark:bg-gray-800 rounded-full group-hover:bg-blue-100 dark:group-hover:bg-blue-900/30 transition-colors duration-200">
                        <Icon icon="lucide:arrow-right" className="w-4 h-4 text-gray-600 dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400" />
                      </div>
                    </Link>
                  ) : (
                    <div></div>
                  )}
                </div>
              </div>
            )}
          </footer>
        </div>
      </article>
    </div>
  );
}